import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';

interface ModuleProtectedRouteProps {
  children: React.ReactNode;
  moduleName: string; // 'Stock' | 'Config' | 'Admin'
  // When provided, access is checked against this specific mini-module instead
  // of module-level access. This prevents a user who only has ONE mini-module of
  // a module (e.g. Stock > "Demandes Achat") from reaching sibling pages such as
  // Stock > "Stock Entry" that they have no permission for.
  miniModuleName?: string;
  action?: string; // operation to require on the mini-module (default 'view')
}

const ModuleProtectedRoute: React.FC<ModuleProtectedRouteProps> = ({
  children,
  moduleName,
  miniModuleName,
  action = 'view',
}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { canAccessModule, hasPermission, loading: permLoading } = usePermissions();
  const location = useLocation();

  if (authLoading || permLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Mini-module guard takes precedence: require the specific permission.
  // Otherwise fall back to module-level access.
  const authorized = miniModuleName
    ? hasPermission(moduleName, action, miniModuleName)
    : canAccessModule(moduleName);

  if (!authorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ModuleProtectedRoute;
